import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ShoppingCart, User, Search, Menu, X } from 'lucide-react';

const Header22 = ({ cartCount = 0 }) => {
    const navigate = useNavigate();
    const [scrolled, setScrolled] = useState(false);
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

    useEffect(() => {
        const handleScroll = () => {
            setScrolled(window.scrollY > 50);
        };
        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    useEffect(() => {
        if (mobileMenuOpen) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = '';
        }
        return () => {
            document.body.style.overflow = '';
        };
    }, [mobileMenuOpen]);

    const navLinks = [
        { label: 'Home', path: '/template22' },
        { label: 'Collections', path: '/template22/collections' },
        { label: 'Craft', path: '/template22/craft' },
        { label: 'Our Story', path: '/template22/about' },
        { label: 'Shop', path: '/template22/shop' },
        { label: 'Contact', path: '/template22/contact' },
        { label: 'Orders', path: '/template22/orders' },
    ];

    const handleNavClick = (path) => {
        navigate(path);
        setMobileMenuOpen(false);
    };

    return (
        <>
            <style>{`
                @import url('https://fonts.googleapis.com/css2?family=Cormorant+Infant:ital,wght@0,300;0,400;0,500;0,600;0,700;1,400&family=Source+Sans+3:wght@300;400;500;600;700&display=swap');
            `}</style>

            <header
                style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    zIndex: 50,
                    backgroundColor: scrolled ? '#FAF6F1' : 'transparent',
                    borderBottom: scrolled ? '1px solid #E8DDD0' : '1px solid transparent',
                    transition: 'background-color 0.3s ease, border-color 0.3s ease, box-shadow 0.3s ease',
                    boxShadow: scrolled ? '0 2px 12px rgba(92,61,46,0.06)' : 'none',
                }}
            >
                <div
                    style={{
                        maxWidth: '1400px',
                        margin: '0 auto',
                        padding: '0 24px',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        height: '80px',
                    }}
                >
                    {/* Left: Brand */}
                    <h1
                        onClick={() => navigate('/template22')}
                        style={{
                            fontFamily: "'Cormorant Infant', Georgia, serif",
                            fontSize: '28px',
                            fontWeight: 600,
                            color: '#5C3D2E',
                            cursor: 'pointer',
                            letterSpacing: '5px',
                            margin: 0,
                            textTransform: 'uppercase',
                        }}
                    >
                        MAATI
                    </h1>

                    {/* Center: Nav Links (Desktop) */}
                    <nav
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '32px',
                        }}
                        className="maati-desktop-nav"
                    >
                        {navLinks.map((link) => (
                            <button
                                key={link.label}
                                onClick={() => handleNavClick(link.path)}
                                style={{
                                    fontFamily: "'Source Sans 3', -apple-system, sans-serif",
                                    fontSize: '13px',
                                    fontWeight: 500,
                                    color: '#5C3D2E',
                                    background: 'none',
                                    border: 'none',
                                    cursor: 'pointer',
                                    padding: 0,
                                    letterSpacing: '1.5px',
                                    textTransform: 'uppercase',
                                    transition: 'color 0.3s ease',
                                }}
                                onMouseEnter={(e) => (e.target.style.color = '#C4572A')}
                                onMouseLeave={(e) => (e.target.style.color = '#5C3D2E')}
                            >
                                {link.label}
                            </button>
                        ))}
                    </nav>

                    {/* Right: Icons */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
                        <Search
                            size={20}
                            style={{ color: '#5C3D2E', cursor: 'pointer', transition: 'color 0.3s ease' }}
                            onMouseEnter={(e) => (e.currentTarget.style.color = '#C4572A')}
                            onMouseLeave={(e) => (e.currentTarget.style.color = '#5C3D2E')}
                        />
                        <User
                            size={20}
                            style={{ color: '#5C3D2E', cursor: 'pointer', transition: 'color 0.3s ease' }}
                            onClick={() => navigate('/template22/profile')}
                            onMouseEnter={(e) => (e.currentTarget.style.color = '#C4572A')}
                            onMouseLeave={(e) => (e.currentTarget.style.color = '#5C3D2E')}
                        />
                        <div
                            style={{ position: 'relative', cursor: 'pointer' }}
                            onClick={() => navigate('/template22/cart')}
                        >
                            <ShoppingCart
                                size={20}
                                style={{ color: '#5C3D2E', transition: 'color 0.3s ease' }}
                                onMouseEnter={(e) => (e.currentTarget.style.color = '#C4572A')}
                                onMouseLeave={(e) => (e.currentTarget.style.color = '#5C3D2E')}
                            />
                            {cartCount > 0 && (
                                <span
                                    style={{
                                        position: 'absolute',
                                        top: '-8px',
                                        right: '-8px',
                                        backgroundColor: '#C4572A',
                                        color: '#FFFFFF',
                                        fontSize: '10px',
                                        fontWeight: 700,
                                        fontFamily: "'Source Sans 3', -apple-system, sans-serif",
                                        width: '18px',
                                        height: '18px',
                                        borderRadius: '50%',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                    }}
                                >
                                    {cartCount}
                                </span>
                            )}
                        </div>

                        {/* Mobile Hamburger */}
                        <button
                            className="maati-mobile-menu-btn"
                            onClick={() => setMobileMenuOpen(true)}
                            style={{
                                display: 'none',
                                background: 'none',
                                border: 'none',
                                cursor: 'pointer',
                                padding: 0,
                            }}
                        >
                            <Menu size={24} style={{ color: '#5C3D2E' }} />
                        </button>
                    </div>
                </div>
            </header>

            {/* Mobile Full-Screen Overlay */}
            <div
                style={{
                    position: 'fixed',
                    inset: 0,
                    zIndex: 100,
                    backgroundColor: '#FAF6F1',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    opacity: mobileMenuOpen ? 1 : 0,
                    pointerEvents: mobileMenuOpen ? 'auto' : 'none',
                    transition: 'opacity 0.3s ease',
                }}
            >
                {/* Close Button */}
                <button
                    onClick={() => setMobileMenuOpen(false)}
                    style={{
                        position: 'absolute',
                        top: '24px',
                        right: '24px',
                        background: 'none',
                        border: 'none',
                        cursor: 'pointer',
                        padding: 0,
                    }}
                >
                    <X size={28} style={{ color: '#5C3D2E' }} />
                </button>

                {/* Brand */}
                <h2
                    style={{
                        fontFamily: "'Cormorant Infant', Georgia, serif",
                        fontSize: '24px',
                        fontWeight: 600,
                        color: '#C4572A',
                        letterSpacing: '5px',
                        marginBottom: '40px',
                    }}
                >
                    MAATI
                </h2>

                <nav style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '28px' }}>
                    {navLinks.map((link) => (
                        <button
                            key={link.label}
                            onClick={() => handleNavClick(link.path)}
                            style={{
                                fontFamily: "'Cormorant Infant', Georgia, serif",
                                fontSize: '24px',
                                fontWeight: 500,
                                color: '#5C3D2E',
                                background: 'none',
                                border: 'none',
                                cursor: 'pointer',
                                padding: 0,
                                letterSpacing: '3px',
                                textTransform: 'uppercase',
                                transition: 'color 0.3s ease',
                            }}
                            onMouseEnter={(e) => (e.target.style.color = '#C4572A')}
                            onMouseLeave={(e) => (e.target.style.color = '#5C3D2E')}
                        >
                            {link.label}
                        </button>
                    ))}
                </nav>

                {/* Decorative Element */}
                <div
                    style={{
                        marginTop: '40px',
                        width: '40px',
                        height: '1px',
                        backgroundColor: '#C4572A',
                    }}
                />
                <p
                    style={{
                        marginTop: '16px',
                        fontFamily: "'Cormorant Infant', Georgia, serif",
                        fontSize: '14px',
                        fontStyle: 'italic',
                        color: '#8B7355',
                    }}
                >
                    Handcrafted with heritage
                </p>
            </div>

            {/* Responsive Styles */}
            <style>{`
                .maati-desktop-nav {
                    display: flex !important;
                }
                .maati-mobile-menu-btn {
                    display: none !important;
                }
                @media (max-width: 768px) {
                    .maati-desktop-nav {
                        display: none !important;
                    }
                    .maati-mobile-menu-btn {
                        display: block !important;
                    }
                }
            `}</style>
        </>
    );
};

export default Header22;
